<template>
  <div class="my-3">
    <div :class="['overlay', {active: isLoading}]">
      <loading-spinner
        custom-id="loading_spinner_table_view"
        :loading-status="isLoading"
      />
    </div>
    <ejs-grid
      id="templateGrid"
      ref="grid"
      class="cendyn-grid sortingenabled"
      :allow-paging="true"
      :allow-filtering="true"
      :page-settings="pageSettings"
      :filter-settings="filterOptions"
      :allow-sorting="true"
      :sort-settings="sortOptions"
      :data-source="templates"
      :data-bound="dataBound"
      :before-data-bound="beforeDataBound"
      :data-state-change="dataStateChange"
      :action-begin="actionBegin"
      :action-complete="actionComplete"
      clip-mode="EllipsisWithTooltip"
    >
      <e-columns>
        <e-column
          field="id"
          header-text="Id"
          :visible="false"
        />
        <e-column
          field="selected"
          header-text=" "
          :template="selectedTemplate"
          :allow-filtering="false"
          :allow-sorting="false"
          :visible="isSearchingTemplate"
          width="40"
        />
        <e-column
          field="image"
          header-text=" "
          :template="imageTemplate"
          :allow-filtering="false"
          :allow-sorting="false"
          width="85"
        />
        <e-column
          field="name"
          header-text="Name"
          :template="nameEditorTemplate"
          width="300"
          :filter="customFilter"
        />
        <e-column
          field="editorTypeId"
          header-text="Editor"
          :template="editorTypeTemplate"
          :filter="editorFilter"
          width="140"
        />
        <e-column
          field="tags"
          header-text="Tags"
          clip-mode="Ellipsis"
          :template="tagsTemplate"
          :filter="tagsFilter"
          width="233"
        />
        <e-column
          field="active"
          header-text="Status"
          width="140"
          :template="statusTemplate"
          :filter="statusFilter"
        />
        <e-column
          field="childTemplates"
          header-text="Campaigns"
          width="150"
          :template="campaignsTemplate"
          :filter="customNumberFilter"
        />
        <e-column
          field="updateDate"
          header-text="Updated"
          width="230"
          type="string"
          :sort-comparer="sortComparer"
          :template="updatedTemplate"
          :filter="customFilter"
        />
        <e-column
          field="updatedBy"
          header-text="updatedBy"
          :visible="false"
        />
        <e-column
          field=" "
          header-text=""
          :template="actionTemplate"
          :allow-filtering="false"
          :allow-sorting="false"
          width="40"
        />
      </e-columns>
    </ejs-grid>
    <BaseDialog
      ref="deactivateDialog"
      :content="'Are you sure you wish to deactivate this template? <br /> You will not be able to use within an email campaign unless the template is active.'"
      :submit-button="deactivateButton"
    />
    <BaseDialog
      ref="activateDialog"
      :content="'Are you sure you wish to activate this template?'"
      :submit-button="activateButton"
    />
    <BaseDialog
      ref="previewTemplateDialog"
      title="Preview"
      :is-iframe="true"
      :content="templateContent"
      :custom-content-template="customContentTemplate"
      :on-close-dialog="onCloseDialog"
      width="90%"
      height="90%"
    />
  </div>
</template>

<script>
import { Page, Sort, Filter } from '@syncfusion/ej2-vue-grids'
import { mapGetters, mapState } from 'vuex'
import { GridImage, GridNameEditor, GridTags, GridStatus, GridCampaigns, GridUpdated, GridAction, GridSelectedTemplate, GridEditor } from '@/components/Grid'
import { MultiSelect, CheckBoxSelection, DropDownList } from '@syncfusion/ej2-dropdowns'
import BaseDialog from '@/components/Dialog/BaseDialog'
import LoadingSpinner from '@/components/LoadingSpinner'
import localeFormat from '@/constants/dateFormat'
import { createElement } from '@syncfusion/ej2-base'
import { TextBox, MaskedTextBox } from '@syncfusion/ej2-inputs'
import moment from 'moment'
import $ from 'jquery'
import TemplatePreview from '../components/TemplatePreview'

MultiSelect.Inject(CheckBoxSelection)

export default {
  components: {
    BaseDialog,
    LoadingSpinner
  },
  provide () {
    return {
      grid: [Filter, Sort, Page],
      actions: () => ({
        details: this.details,
        edit: this.edit,
        clone: this.clone,
        remove: this.remove
      })
    }
  },
  props: {
    details: Function,
    edit: Function,
    clone: Function,
    remove: Function
  },
  data () {
    return {
      customContentTemplate: TemplatePreview,
      pageSizes: [10, 25, 50, 100],
      pageSettings: null,
      sortOptions: { columns: [{ field: 'updateDate', direction: 'Descending' }] },
      filterOptions: {
        type: 'Menu',
        mode: 'OnEnter',
        operators: {
          stringOperator: [
            { value: 'startsWith', text: 'Starts With' },
            { value: 'endsWith', text: 'Ends With' },
            { value: 'contains', text: 'Contains' },
            { value: 'equal', text: 'Equal' }
          ]
        }
      },
      filter: {
        type: 'CheckBox'
      },
      editorTypes: [{ description: 'Studio Editor', status: 'Studio Editor', value: 'Studio Editor' },
        { description: 'Classic Drag-and-Drop', status: 'Classic Drag-and-Drop', value: 'Classic Drag & Drop' },
        { description: 'Custom Code', status: 'Custom Code', value: 'Custom Code' }],
      customFilter: {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', {
              className: 'flm-input'
            })
            args.target.appendChild(flValInput)
            this.dropInstance = new TextBox({
              placeholder: 'Enter the value'
            })
            this.dropInstance.appendTo(flValInput)
            this.$helpers.setFilterId(this.dropInstance.element)
          },
          write: (args) => {
            if (args.filteredValue) {
              this.dropInstance.value = args.filteredValue
            }
          },
          read: (args) => {
            args.fltrObj.filterByColumn(
              args.column.field,
              args.operator,
              this.dropInstance.value,
              false
            )
          }
        }
      },
      customNumberFilter: {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', {
              className: 'flm-input'
            })
            args.target.appendChild(flValInput)
            this.dropInstance = new MaskedTextBox({
              placeholder: 'Enter the value',
              mask: '[0-9][0-9][0-9][0-9]',
              promptChar: ' ',
              focus: this.specifiedfocus
            })
            this.dropInstance.appendTo(flValInput)
            this.$helpers.setFilterId(this.dropInstance.element)
          },
          write: (args) => {
            if (args.filteredValue) {
              this.dropInstance.value = args.filteredValue
            }
          },
          read: (args) => {
            args.fltrObj.filterByColumn(
              args.column.field,
              args.operator,
              this.dropInstance.value,
              false
            )
          }
        }
      },
      tagsFilter: this.getTagsFilter(),
      statusFilter: this.getStatusFilter(),
      editorFilter: this.getEditorFilter(),
      dateTimeFormat: 'M/D/YYYY hh:mm A',
      filters: [],
      deactivateButton: {
        label: 'Deactivate',
        class: 'btn-danger',
        action: this.deactivateAction
      },
      activateButton: {
        label: 'Activate',
        class: 'btn-success',
        action: this.activateAction
      },
      imageTemplate () {
        return {
          template: {
            extends: GridImage
          }
        }
      },
      nameEditorTemplate () {
        return {
          template: {
            extends: GridNameEditor
          }
        }
      },
      tagsTemplate () {
        return {
          template: {
            extends: GridTags
          }
        }
      },
      statusTemplate () {
        return {
          template: {
            extends: GridStatus
          }
        }
      },
      campaignsTemplate () {
        return {
          template: {
            extends: GridCampaigns
          }
        }
      },
      updatedTemplate () {
        return {
          template: {
            extends: GridUpdated
          }
        }
      },
      actionTemplate () {
        return {
          template: {
            extends: GridAction
          }
        }
      },
      selectedTemplate () {
        return {
          template: {
            extends: GridSelectedTemplate
          }
        }
      },
      editorTypeTemplate () {
        return {
          template: {
            extends: GridEditor
          }
        }
      },
      rowsBound: false
    }
  },
  computed: {
    ...mapState({
      templates: state => state.templateBuilder.templates,
      gridStateChangeData: state => state.templateBuilder.gridStateChangeData,
      tags: (state) => {
        const result = state.templateBuilder.tags.map(tag => tag.name.trim())
          .flat()
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort()
          .map(tag => { return { description: tag, value: tag } })
        return result
      }
    }),
    ...mapGetters({
      isLoading: 'getLoadingStatus',
      paging: 'getPaging',
      templateContent: 'getTemplateContent',
      getSavedGridEditorFilter: 'getSavedGridEditorFilter'
    }),
    isSearchingTemplate () {
      return this.$route.query.isSearchingTemplate === 'true'
    },
    status () {
      const statuses = [{ description: 'Active', status: true }]
      if (this.$route.query.isSearchingTemplate !== 'true') {
        statuses.push({ description: 'Inactive', status: false })
      }
      return statuses
    }
  },
  watch: {
    templates () {
    },
    locale () {
      this.dateTimeFormat = localeFormat.dateFormat(this.locale) + ' ' + localeFormat.timeFormat(this.locale)
    },
    getSavedGridEditorFilter () {
      // Watching when a editor grid filter is changed and updating the column accordingly
      this.validateEditorColumn()
    }
  },
  created () {
    const pageSize = this.getPageSizes()
    this.pageSettings = { pageCount: this.paging.pageCount, pageSize: pageSize, pageSizes: this.pageSizes }
    this.$store.commit('SET_PAGING', { ...this.paging, pageSizes: this.pageSizes, pageSize: pageSize })
  },
  mounted () {
    this.$store.dispatch('setGridStateChangeData', { where: null })
    window.onresize = (event) => {
      this.transformTagsContent()
    }
    this.refresh()
    this.loadTemplates()
    this.$helpers.setTableId('templateGrid')
    this.$helpers.setEjsPager('templateGrid')
    this.validateEditorColumn()
  },
  methods: {
    refresh () {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      if (userEmail) {
        const { paging } = this
        const { order } = paging
        const dataState = {
          action: {
            currentPage: paging.page
          },
          skip: 0,
          take: paging.pageSize,
          sorted: [
            {
              name: order.column,
              direction: order.direction
            }
          ]
        }
        this.$store.dispatch('setTemplates', { userEmail, dataState })
      }
    },
    specifiedfocus (args) {
      args.selectionStart = 0
      args.selectionEnd = 0
    },
    onCloseDialog () {
      this.$store.commit('SET_TEMPLATE_PREVIEW_CONTENT', null)
    },
    getTagsFilter () {
      return this.getArrayFilter('Select a tag', 'tags')
    },
    getArrayFilter (placeHolder, propertyName) {
      return {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', {
              className: 'flm-input'
            })
            args.target.appendChild(flValInput)
            if (args.target.parentElement.children) {
              args.target.parentElement.children[0].style.display = 'none'
              args.target.parentElement.children[1].style.paddingTop = '0px'
            }
            this.dropInstance = new MultiSelect({
              dataSource: this[propertyName],
              fields: {
                text: 'description',
                value: 'value'
              },
              placeholder: placeHolder,
              popupHeight: '200px',
              mode: 'CheckBox'
            })
            this.dropInstance.value = this.dropInstance.value || []
            this.dropInstance.appendTo(flValInput)
          },
          write: (args) => {
            let arr = []
            if (
              args.parent.filterModule.actualPredicate
                .tags !== undefined
            ) {
              arr = args.parent.filterModule.actualPredicate.tags.map(
                (element) => element.value
              )
            }
            this.dropInstance.value = Array.isArray(args.filteredValue)
              ? args.filteredValue
              : arr
          },
          read: (args) => {
            args.fltrObj.filterSettings.columns = args.fltrObj.filterSettings.columns.filter(
              (x) => x.field !== args.column.field
            )
            args.fltrObj.filterByColumn(
              args.column.field,
              args.operator,
              this.dropInstance.value
            )
          }
        }
      }
    },
    getEditorFilter () {
      return {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', {
              className: 'flm-input'
            })
            args.target.appendChild(flValInput)
            if (args.target.parentElement.children) {
              args.target.parentElement.children[0].style.display = 'none'
              args.target.parentElement.children[1].style.paddingTop = '0px'
            }
            this.dropInstance = new MultiSelect({
              dataSource: this.editorTypes,
              fields: {
                text: 'description',
                value: 'value'
              },
              placeholder: 'Select a value',
              popupHeight: '200px',
              mode: 'CheckBox'
            })
            this.dropInstance.value = this.dropInstance.value || []
            this.dropInstance.appendTo(flValInput)
          },
          write: (args) => {
            // Setting the dropinstance to the currently selected studio editor filter
            this.dropInstance.value = this.$store.getters.getSavedGridEditorFilter
            const clearButtonElement = document.querySelectorAll('.e-filter-popup .e-footer-content .e-flmenu-cancelbtn')
            // Adding a custom event listener to reset editor filter on grid if user hits clear button
            const that = this
            clearButtonElement[0].addEventListener('click', function () {
              that.$store.commit('SET_SAVED_GRID_EDITOR_FILTER', [])
              that.loadTemplates()
            })
          },
          read: (args) => {
            // Updating the editor grid filters based on the dropdown and refreshing grid
            this.$store.commit('SET_SAVED_GRID_EDITOR_FILTER', this.dropInstance.value)
            this.refresh()
            this.validateEditorColumn()
          }
        }
      }
    },
    getStatusFilter () {
      return {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', {
              className: 'flm-input'
            })
            args.target.appendChild(flValInput)
            if (args.target.parentElement.children) {
              args.target.parentElement.children[0].style.display = 'none'
              args.target.parentElement.children[1].style.paddingTop = '0px'
            }

            this.dropInstance = new DropDownList({
              dataSource: this.status,
              fields: { text: 'description', value: 'status' },
              placeholder: 'Select a value',
              popupHeight: '200px'
            })
            this.dropInstance.appendTo(flValInput)
          },
          write: (args) => {
            this.dropInstance.value = args.filteredValue
          },
          read: (args) => {
            args.fltrObj.filterByColumn(args.column.field, args.operator, this.dropInstance.value)
          }
        }
      }
    },
    colorFilteredColumns () {
      this.filters = this.$refs.grid.ej2Instances.filterSettings.columns
      if (this.filters) {
        const that = this
        this.$refs.grid.ej2Instances.columns = this.$refs.grid.ej2Instances.columns.map(
          (column) => {
            if (that.filters.find((filter) => filter.field === column.field)) {
              column.customAttributes = { class: 'filtered' }
            } else if (column.customAttributes) {
              delete column.customAttributes
            }
            return column
          }
        )
      }
      this.validateEditorColumn()
    },
    validateEditorColumn () {
      this.$refs.grid.ej2Instances.columns = this.$refs.grid.ej2Instances.columns.map(
        (column) => {
          if (column.field === 'editorTypeId') {
            if (this.$store.getters.getSavedGridEditorFilter && this.$store.getters.getSavedGridEditorFilter.length) {
              column.customAttributes = { class: 'filtered' }
            } else if (column.customAttributes) {
              delete column.customAttributes
            }
          }
          return column
        }
      )
    },
    sortComparer (reference, comparer) {
      const dateTimeFormat = this.dateTimeFormat || 'M/D/YYYY hh:mm A'
      const a = moment(reference, dateTimeFormat)
      const b = moment(comparer, dateTimeFormat)
      return a.valueOf() - b.valueOf()
    },
    actionBegin: function (args) {
      if (
        args.action === 'filtering' ||
        args.action === 'refresh' ||
        args.action === 'paging'
      ) {
        this.colorFilteredColumns()
      }
    },
    actionComplete: function (args) {
      if (
        args.action === 'clearFilter' ||
        args.action === 'clear-filter' ||
        args.action === 'filter'
      ) {
        this.colorFilteredColumns()
      }
    },
    getPageSizes () {
      const pageSizeIndex = this.paging.pageSizes.findIndex(x => parseInt(x) === this.paging.pageSize)
      return (pageSizeIndex > -1) ? this.pageSizes[pageSizeIndex] : 10
    },
    loadTemplates () {
      const state = {
        action: {
          currentPage: 1
        },
        skip: 0,
        take: this.pageSettings.pageSize
      }
      if (this.gridStateChangeData) {
        this.$refs.grid.ej2Instances.filterSettings = Object.assign({}, this.$refs.grid.ej2Instances.filterSettings, { columns: this.gridStateChangeData.dataState })
        this.dataStateChange(this.gridStateChangeData.state, true)
      } else {
        this.dataStateChange(state, true)
      }
    },
    dataStateChange (dataState, isInit) {
      this.$store.commit('SET_SELECTED_TEMPLATE', null)

      this.$refs.grid.hideSpinner()
      if (dataState.action && dataState.action.requestType !== 'stringfilterrequest' && dataState.action.requestType !== 'refresh') {
        if (!isInit) {
          const config = this.$refs.grid.ej2Instances.filterSettings.columns.reduce((cnf, column) => {
            cnf.push({
              field: column.field,
              matchCase: column.matchCase,
              operator: column.operator,
              predicate: column.predicate,
              value: column.value
            })
            return cnf
          }, [])
          this.$store.dispatch('setGridStateChangeData', { where: { dataState: config, state: dataState } })
        }

        const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
        const paging = {}
        paging.page = dataState.action.currentPage ? dataState.action.currentPage : 1
        paging.pageSize = dataState.take ? dataState.take : 12
        if (dataState.sorted && dataState.sorted.length) {
          const sort = dataState.sorted[0]
          paging.order = {
            column: sort.name,
            direction: sort.direction === 'descending' ? 'desc' : 'asc'
          }
        }
        this.$store.commit('SET_PAGING', paging)
        if (dataState.action.requestType) {
          this.$store.commit('SET_PAGING', { page: 1 })
        }
        if (userEmail) {
          this.$store.dispatch('setTemplates', { userEmail, dataState })
            .then(response => {
              this.gridLoaded = true
            })
        }
        this.transformTagsContent()
      }
    },
    dataBound (obj) {
      // console.log('dataBound: ', obj)
      this.$refs.grid.hideSpinner()
      this.formatPagination()
      this.afterDataBound()
      this.transformTagsContent()
    },
    beforeDataBound (obj) {
      // console.log('beforeDataBound: ', obj)
      this.rowsBound = obj && obj.actionArgs && obj.actionArgs.requestType === 'refresh'
    },
    afterDataBound () {
      setTimeout(function () {
        if (this.templates && this.templates.count === 0 && this.rowsBound === true) {
          $('.e-emptyrow').find('td').text('No records to display')
        }
      }, 3000)
    },
    formatPagination () {
      // Timeout is to avoid conflict with the component re-rendering
      setTimeout(() => {
        const pageNo = document.getElementsByClassName('e-pagenomsg')
        const pageCount = document.getElementsByClassName('e-pagecountmsg')
        if (pageNo[0] && pageCount[0]) {
          pageNo[0].innerHTML = this.$helpers.numberFormat(pageNo[0].innerHTML)
          pageCount[0].innerHTML = this.$helpers.numberFormat(pageCount[0].innerHTML)
        }
      }, 10)
    },
    transformTagsContent () {
      // TODO: Remove this transformation
      /* document.getElementsByClassName('grid-cell-tags').forEach((e) => {
        const tags = e.getElementsByClassName('tags').item(0)
        if (tags) {
          const tagsArray = tags.innerHTML.split(', ')
          const tagsContent = e.getElementsByClassName('tagsContent').item(0)
          const averageLetterWidth = tags.offsetWidth / tags.innerHTML.length
          const tagsShowing = []
          const tagsNotShowing = []
          let lettersWidth = 0
          if (e.scrollWidth > e.offsetWidth || tags.offsetWidth > e.offsetWidth) {
            tagsArray.forEach((tag, index) => {
              if (tag.length) {
                lettersWidth += tag.length * averageLetterWidth + (averageLetterWidth * 2)
                if (lettersWidth < e.offsetWidth || index === 0) {
                  tagsShowing.push(tag)
                } else {
                  tagsNotShowing.push(tag)
                }
              }
            })
            tagsContent.innerHTML = (tagsShowing.length ? tagsShowing.join(', ') : '') + (tagsNotShowing.length ? ' +' + tagsNotShowing.length : '')
          } else {
            tagsContent.innerHTML = tags.innerHTML
          }
        }
      }) */
    },
    deactivateAction () {
      this.changeStatusTemplate(false)
    },
    activateAction () {
      this.changeStatusTemplate(true)
    },
    refreshTemplates () {
      const templateId = this.$store.getters.getGridSelectedTemplate.id
      const userEmail = atob(this.$route.query.userEmail)
      const showLoading = false
      if (templateId) {
        this.$store.dispatch('setTemplates', { userEmail, showLoading })
        this.$store.dispatch('setTemplateSummary', { templateId, userEmail, showLoading })
        this.$store.dispatch('setTemplateHistory', { templateId, userEmail, showLoading })
      }
    },
    changeStatusTemplate (state) {
      const encodedUrlQuery = this.$helpers.getTemplateBuilderQuery(this.$route, false)
      const unencodedUrlQuery = { ...encodedUrlQuery }
      const userEmail = atob(unencodedUrlQuery.userEmail)
      const templateId = this.$store.getters.getGridSelectedTemplate.id
      const response = this.$store.dispatch('activeTemplate', { templateId, active: state, userEmail })
      response.then(({ success, error }) => {
        if (success) {
          this.refreshTemplates()
        } else if (error) {
          console.log(error)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.e-pager{
  padding: 0 !important;
}
.overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: none;
    background-color: #ffffffcf;
    &.active {
      display: block;
    }
  }
</style>
